import { useMemo, useState } from 'react';

import { Input } from '../../components/input';
import { cn } from '../../lib/utils';
import type { CheckboxListOption } from '../checkbox-list';
import { CheckboxList } from '../checkbox-list';

export type { CheckboxListOption };

interface SearchableCheckboxListProps {
  options: CheckboxListOption[];
  /** Selected value ids. Controlled — query is internal view-state only. */
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  searchPlaceholder?: string;
  maxHeight?: number;
  rowHeight?: number;
  emptyLabel?: string;
  /** Maps to the search input's `data-testid`. */
  testId?: string;
}

/**
 * `CheckboxList` with a search box that client-side filters by label substring.
 * The list height animates as the result set narrows (transition lives on the
 * `CheckboxList` scroll container), and the list scrolls back to the top on each
 * query change so newly matching rows aren't hidden below a stale scroll position.
 */
export const SearchableCheckboxList = ({
  options,
  value,
  onChange,
  disabled = false,
  searchPlaceholder = 'Search',
  maxHeight,
  rowHeight,
  emptyLabel = 'No matches found',
  testId,
}: SearchableCheckboxListProps) => {
  const [query, setQuery] = useState('');

  const filteredOptions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(trimmed)
    );
  }, [options, query]);

  return (
    <div
      data-slot={'searchable-checkbox-list'}
      className={cn('flex flex-col gap-2')}
    >
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={searchPlaceholder}
        disabled={disabled}
        data-testid={testId}
      />
      {/* a11y: native checkbox tabbing is preserved, but the virtualiser only
          mounts (and so only tabs through) visible rows. This polite live region
          announces the filtered result count as the user searches, covering that
          virtualised-tab-order limitation for screen-reader users. */}
      <span aria-live={'polite'} className={cn('sr-only')}>
        {`${filteredOptions.length} ${
          filteredOptions.length === 1 ? 'result' : 'results'
        }`}
      </span>
      <CheckboxList
        options={filteredOptions}
        value={value}
        onChange={onChange}
        disabled={disabled}
        maxHeight={maxHeight}
        rowHeight={rowHeight}
        emptyLabel={emptyLabel}
        scrollResetKey={query}
      />
    </div>
  );
};

export type { SearchableCheckboxListProps };
